import React, { useEffect, useState } from "react";
import { X, Banknote, CreditCard, Layers3, Wallet, User2, Smartphone } from "lucide-react";
import { TID } from "@/constants/testIds";

const METHODS = [
  { key: "efectivo", label: "Efectivo",  icon: Banknote,    tid: TID.payMethodCash,  color: "bg-emerald-600" },
  { key: "tarjeta",  label: "Tarjeta · Redsys", icon: CreditCard, tid: TID.payMethodCard,  color: "bg-blue-600" },
  { key: "bizum",    label: "Bizum",     icon: Smartphone,  tid: "payment-method-bizum",   color: "bg-sky-500" },
  { key: "vale",     label: "Vale",      icon: Wallet,      tid: "payment-method-voucher", color: "bg-amber-500" },
  { key: "credito",  label: "Crédito cliente", icon: User2, tid: "payment-method-credit",  color: "bg-slate-700" },
];

export default function PaymentModal({ sale, onClose, onConfirm, pendiente }) {
  const total = (pendiente !== undefined ? pendiente : sale?.total) || 0;
  const [method, setMethod] = useState("efectivo");
  const [amount, setAmount] = useState(total.toFixed(2));
  const [splits, setSplits] = useState([]); // mixed payments
  const [mixed, setMixed] = useState(false);

  useEffect(() => {
    setAmount(total.toFixed(2));
    setSplits([]);
    setMixed(false);
    setMethod("efectivo");
  }, [total]);

  const remaining = total - splits.reduce((a, b) => a + b.amount, 0);
  const numAmount = parseFloat(amount || "0") || 0;
  const change = method === "efectivo" && !mixed ? Math.max(numAmount - total, 0) : 0;

  const kp = (n) => {
    if (n === "C") return setAmount("0.00");
    if (n === "←") return setAmount((amount.length > 1 ? amount.slice(0, -1) : "0"));
    if (n === "EXACT") return setAmount((mixed ? remaining : total).toFixed(2));
    setAmount((prev) => (prev === "0.00" || prev === "0" ? String(n) : prev + String(n)));
  };

  const confirm = () => {
    if (mixed) {
      const allPays = [...splits];
      const last = parseFloat(amount || "0") || 0;
      if (last > 0) allPays.push({ method, amount: last });
      onConfirm(allPays);
    } else {
      onConfirm([{ method, amount: numAmount }]);
    }
  };

  const addSplit = () => {
    const a = parseFloat(amount || "0") || 0;
    if (a <= 0) return;
    setSplits([...splits, { method, amount: Math.min(a, remaining) }]);
    setAmount((remaining - a).toFixed(2));
  };

  const canConfirm = !mixed
    ? numAmount + 0.005 >= total
    : (splits.reduce((a, b) => a + b.amount, 0) + numAmount) + 0.005 >= total;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid={TID.payModal} className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex">
        {/* LEFT: methods + summary */}
        <div className="w-[300px] bg-slate-900 text-white p-6 flex flex-col">
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/60">Importe a cobrar</div>
          <div className="font-mono text-5xl font-semibold mt-2">{total.toFixed(2)}€</div>
          <div className="mt-1 text-xs text-white/60">{sale?.lines?.length || 0} artículos</div>

          <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-white/60">Forma de pago</div>
          <div className="grid grid-cols-1 gap-2 mt-2">
            {METHODS.map((m) => {
              const Icon = m.icon;
              const active = method === m.key;
              return (
                <button
                  key={m.key}
                  data-testid={m.tid}
                  onClick={() => setMethod(m.key)}
                  className={`h-12 rounded-md text-sm font-medium flex items-center gap-3 px-3 transition-colors ${
                    active ? "bg-white text-slate-900" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  <span className={`h-7 w-7 rounded-md flex items-center justify-center ${active ? m.color + " text-white" : "bg-white/10"}`}>
                    <Icon size={16} />
                  </span>
                  {m.label}
                </button>
              );
            })}
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
            <input
              data-testid={TID.payMethodMixed}
              type="checkbox"
              checked={mixed}
              onChange={(e) => {
                setMixed(e.target.checked);
                setSplits([]);
                setAmount(total.toFixed(2));
              }}
            />
            <Layers3 size={14} /> Pago mixto
          </label>

          {mixed && splits.length > 0 && (
            <div className="mt-3 space-y-1 text-xs">
              {splits.map((s, i) => (
                <div key={i} className="flex justify-between bg-white/5 rounded px-2 py-1">
                  <span className="capitalize">{s.method}</span>
                  <span className="font-mono">{s.amount.toFixed(2)}€</span>
                </div>
              ))}
              <div className="flex justify-between font-medium pt-1 border-t border-white/10 mt-1">
                <span>Falta</span>
                <span className="font-mono">{remaining.toFixed(2)}€</span>
              </div>
            </div>
          )}

          <button
            data-testid={TID.payClose}
            onClick={onClose}
            className="mt-auto h-11 rounded-md bg-white/5 hover:bg-white/15 text-sm flex items-center justify-center gap-2"
          >
            <X size={16} /> Cancelar
          </button>
        </div>

        {/* RIGHT: keypad + confirm */}
        <div className="flex-1 p-6 flex flex-col">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Importe entregado</div>
          <div className="mt-2 flex items-end gap-3">
            <input
              data-testid={TID.payAmount}
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(",", "."))}
              className="flex-1 h-20 px-4 text-5xl font-mono font-semibold border-2 border-slate-200 rounded-lg focus:outline-none focus:border-slate-900 text-right"
              inputMode="decimal"
            />
            <button
              onClick={() => kp("EXACT")}
              className="h-20 px-4 rounded-lg border-2 border-slate-200 text-sm font-medium hover:bg-slate-50"
            >
              Importe<br />exacto
            </button>
          </div>

          {change > 0 && (
            <div className="mt-3 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex justify-between items-center">
              <span className="text-sm text-emerald-800">Cambio a devolver</span>
              <span className="font-mono text-2xl font-semibold text-emerald-700">{change.toFixed(2)}€</span>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2 mt-4 flex-1">
            {[7, 8, 9, "C", 4, 5, 6, "←", 1, 2, 3, "."].map((n) => (
              <button
                key={n}
                onClick={() => {
                  if (n === ".") {
                    if (!amount.includes(".")) setAmount(amount + ".");
                  } else {
                    kp(n);
                  }
                }}
                className="kp-btn h-full min-h-[60px] bg-white border border-slate-200 rounded-md text-2xl font-mono hover:bg-slate-100 shadow-sm"
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => kp(0)}
              className="kp-btn col-span-2 h-full min-h-[60px] bg-white border border-slate-200 rounded-md text-2xl font-mono hover:bg-slate-100 shadow-sm"
            >
              0
            </button>
            <button
              onClick={() => kp("00")}
              className="kp-btn h-full min-h-[60px] bg-white border border-slate-200 rounded-md text-2xl font-mono hover:bg-slate-100 shadow-sm"
            >
              00
            </button>
            {mixed ? (
              <button
                onClick={addSplit}
                className="h-full min-h-[60px] bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800"
              >
                Añadir<br />parcial
              </button>
            ) : (
              <button
                onClick={() => kp("EXACT")}
                className="h-full min-h-[60px] bg-slate-200 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-300"
              >
                Exacto
              </button>
            )}
          </div>

          <button
            data-testid={TID.payConfirm}
            onClick={confirm}
            disabled={!canConfirm}
            className="mt-4 h-16 rounded-lg bg-green-600 text-white font-display font-semibold text-xl hover:bg-green-700 disabled:bg-slate-300 transition-colors"
          >
            Confirmar cobro · {total.toFixed(2)}€
          </button>
        </div>
      </div>
    </div>
  );
}
